import React, { useEffect, useMemo, useState } from 'react';
import { QueryClient } from 'react-query';
import { SkipNavLink } from '@reach/skip-nav';
import * as Sentry from '@sentry/nextjs';
import config from 'config';
import { NextPage } from 'next';
import { AppProps } from 'next/app';
import Head from 'next/head';
import ThemeProvider from 'theme';
import { getCSSVars } from 'theme/utils';

import i18nBuilder from '@lyearn/i18n';
import { Snackbar as SnackbarProvider, StylesProvider } from '@lyearn/ui';
import AppContainer from '@/components/AppContainer';
import BreadcrumbContext from '@/components/Breadcrumb/components/BreadcrumbContext';
import ConfigsProvider from '@/components/ConfigsProvider';
import { ErrorBoundary, PageError } from '@/components/Error';
import FreeTrialBanner from '@/components/FreeTrialBanner';
import QueryParamProvider from '@/components/QueryParamProvider';
import { RouteChromeProvider } from '@/components/RouteChrome';
import { RouteChrome } from '@/components/RouteChrome/types';
import { SidebarCollapseProvider } from '@/components/SidebarCollapse';
import FeatureFlagsProvider from '@/helper/FeatureFlags/Provider';
import getClient from '@/helper/urqlClient';
import { AppConfig, AppConfigContext } from '@/hooks/useAppConfig';
import useNavigation from '@/hooks/useNavigation';
import useReloadOnUpdate from '@/hooks/useReloadOnUpdate';
import useSendLocation from '@/hooks/useSendLocation';
import { PersonKnowledgeDiscoveryProvider } from '@/modules/auth/aiOnboarding/PersonKnowledgeDiscoveryProvider';
import { UserProfile } from '@/modules/auth/types';
import BlogMetadata from '@/modules/blogs/BlogMetadata';
import AuthRedirect from '@/pages/auth/AuthRedirect';
import getDefaultLayout from '@/pages/corporate/layout';
import PageResolver from '@/pages/corporate/PageResolver';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { AppRoutes } from '@/routes';
import Bootstrap from '@/routes/Bootstrap';
import ClientProvider from '@/routes/Bootstrap/ClientProvider';
import { FeatureFlagType } from '@/types/schema';
import disableConsole from '@/utils/disableConsole';

import { getRouteChromeDefaults } from './getRouteChromeDefaults';

function SafeHydrate({ children }: { children: React.ReactNode }) {
  return <div suppressHydrationWarning>{typeof window === 'undefined' ? null : children}</div>;
}

const queryClient = new QueryClient();

export type NextPageWithLayout = NextPage & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
  isPublicPage?: () => boolean;
  chrome?: RouteChrome;
};

export type AppPropsWithLayout = AppProps<{
  appConfig: AppConfig;
  user: UserProfile;
  featureFlags: FeatureFlagType[];
  initialBlogContent: any;
}> & {
  Component: NextPageWithLayout;
};

export type AppBodyProps = AppPropsWithLayout & {
  fonts: Record<string, { style: { fontFamily: string } }>;
};

const getUserLanguage = (userLanguages: readonly string[]) => {
  const supportedLanguages = ['en-US', 'es', 'hi', 'ar', 'mr', 'gu', 'kn', 'ta', 'te'];

  if (!userLanguages) {
    return 'en-US';
  }

  const supportedLanguage = userLanguages.find((language) => supportedLanguages.includes(language));

  return supportedLanguage || 'en-US';
};

function AppBody({ Component, pageProps, fonts }: AppBodyProps) {
  const navigation = useNavigation();
  const { appConfig, user, featureFlags, initialBlogContent } = pageProps;

  const isPublicPage = Component?.isPublicPage?.() || false;
  const getLayout = Component?.getLayout || getDefaultLayout(navigation.route);

  const [isI18nInitialized, setI18nInitialized] = useState(false);

  useEffect(() => {
    disableConsole(!!appConfig.isLoggingEnabled);
  }, [appConfig.isLoggingEnabled]);

  useEffect(() => {
    const language = localStorage?.getItem('language');
    i18nBuilder({
      lng: language ?? appConfig?.site.locale ?? getUserLanguage(navigator.languages),
    })
      .then((i18n) => {
        i18n.resolvedLanguage &&
          document.documentElement.setAttribute('lang', i18n.resolvedLanguage!);
        document.documentElement.setAttribute('dir', i18n.dir());
      })
      .finally(() => {
        setI18nInitialized(true);
      });
  }, [appConfig?.site.locale]);

  const isAdminRoute = useAdminRoute();
  const chrome = useMemo(
    () => ({ ...getRouteChromeDefaults(navigation.asPath, isAdminRoute), ...Component?.chrome }),
    [navigation.asPath, isAdminRoute, Component?.chrome],
  );

  useSendLocation();
  useReloadOnUpdate();
  const urqlClient = useMemo(
    () =>
      getClient({
        // Todo breaking usePagination's pageInfo when merging with old page info
        // enableURQLOfflineCache: appConfig.site.enableURQLOfflineCache,
      }),
    // in iframe when switching between admin and learner, urqlClient need to be reset since response is different based on X-learner header
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdminRoute],
  );

  const isBlogPage = navigation.asPath.includes('/blogs/');

  const domain = config.host_domain;

  if (!isI18nInitialized) {
    if (isBlogPage && initialBlogContent) {
      return <BlogMetadata initialBlogContent={initialBlogContent} />;
    }
    return null;
  }

  return (
    <SafeHydrate>
      <React.Suspense fallback={null}>
        <Sentry.ErrorBoundary>
          <Head>
            <style
              dangerouslySetInnerHTML={{
                __html: `
                  :root {
                    ${getCSSVars(fonts, appConfig.theme)}
                  }
              `,
              }}></style>
            <title key="title">{appConfig.site.name}</title>
            {appConfig.site?.favicon ? <link href={appConfig.site?.favicon} rel="icon" /> : null}
            <link href={'/images/lyearn-logo-apple-touch-icon.png'} rel="apple-touch-icon" />
            <meta content={appConfig.site?.name} name="description" />
            <meta content="width=device-width, initial-scale=1" name="viewport" />
          </Head>
          <StylesProvider injectFirst>
            <AppContainer>
              <ThemeProvider
                isDarkModeAllowed={appConfig.ui.isDarkModeAllowed}
                themeConfig={appConfig.theme}>
                <ErrorBoundary fallback={PageError}>
                  <SnackbarProvider>
                    <AppConfigContext.Provider value={appConfig}>
                      <ConfigsProvider>
                        <QueryParamProvider>
                          <AuthRedirect>
                            <PageResolver>
                              <ClientProvider
                                queryClient={queryClient}
                                urqlClient={urqlClient}
                                user={user}>
                                <BreadcrumbContext>
                                  <FeatureFlagsProvider preloadedFeatureFlags={featureFlags}>
                                    <PersonKnowledgeDiscoveryProvider>
                                      <Bootstrap>
                                        <SkipNavLink className="body-short-03" />
                                        <FreeTrialBanner />
                                        <RouteChromeProvider chrome={chrome}>
                                          <SidebarCollapseProvider>
                                            {isPublicPage ? (
                                              getLayout(<Component {...(pageProps as any)} />)
                                            ) : (
                                              <AppRoutes>
                                                {getLayout(<Component {...(pageProps as any)} />)}
                                              </AppRoutes>
                                            )}
                                          </SidebarCollapseProvider>
                                        </RouteChromeProvider>
                                      </Bootstrap>
                                    </PersonKnowledgeDiscoveryProvider>
                                  </FeatureFlagsProvider>
                                </BreadcrumbContext>
                              </ClientProvider>
                            </PageResolver>
                          </AuthRedirect>
                        </QueryParamProvider>
                      </ConfigsProvider>
                    </AppConfigContext.Provider>
                  </SnackbarProvider>
                </ErrorBoundary>
              </ThemeProvider>
            </AppContainer>
          </StylesProvider>
        </Sentry.ErrorBoundary>
      </React.Suspense>
    </SafeHydrate>
  );
}

export default AppBody;
